import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { motion } from 'framer-motion';
import { FaEnvelope, FaHospital, FaCheck, FaTimes } from 'react-icons/fa';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorInvitations = () => {
    const { setTitle } = useTitle();
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [processingInvitation, setProcessingInvitation] = useState(null);

    useEffect(() => {
        setTitle('الدعوات');
        fetchInvitations();
    }, [setTitle, currentPage]);

    const fetchInvitations = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/doctor/my_invitations?page=${currentPage}`);
            
            if (response.data && response.data.status === 'success') {
                // البيانات الآن تأتي من response.data.result.data
                const result = response.data.result;
                const invitationsData = result.data || result;
                
                // التأكد من أن البيانات هي array
                if (Array.isArray(invitationsData)) {
                    setInvitations(invitationsData);
                    
                    // حفظ معلومات الباجينيشن
                    if (result.pagination) {
                        setPagination(result.pagination);
                        setCurrentPage(result.pagination.current_page);
                    }
                } else {
                    setInvitations([]);
                }
            } else {
                throw new Error(response.data?.message || 'فشل في جلب الدعوات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب الدعوات');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'غير محدد';
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'accepted':
                return <span className="badge bg-success">مقبول</span>;
            case 'rejected':
                return <span className="badge bg-danger">مرفوض</span>;
            default:
                return <span className="badge bg-secondary">غير محدد</span>;
        }
    };

    const getSpecializations = (invitation) => {
        if (!invitation) return [];

        const possibleSources = [
            invitation.specializations,
            invitation.specialization,
            invitation.specialities,
            invitation.speciality,
            invitation.category,
            invitation.categories
        ];

        for (const source of possibleSources) {
            if (!source) continue;

            if (Array.isArray(source)) {
                return source
                    .map((item) => {
                        if (typeof item === 'string') return item;
                        if (item && typeof item === 'object') {
                            return item.title || item.name || item.label || '';
                        }
                        return '';
                    })
                    .filter(Boolean);
            }

            if (typeof source === 'string') {
                return source
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }

            if (source && typeof source === 'object') {
                const value = source.title || source.name || source.label;
                if (value) return [value];
            }
        }

        return [];
    };

    const handleInvitationAction = async (invitationId, status) => {
        try {
            setProcessingInvitation(invitationId);
            
            const response = await apiClient.post('/doctor/invitation_action', {
                invetation_id: invitationId,
                status: status
            });

            if (response.data && (response.data.status === 'success' || response.data.statusCode === 200)) {
                showSuccessToast(status === 'accepted' ? 'تم قبول الدعوة بنجاح' : 'تم رفض الدعوة بنجاح');
                fetchInvitations(); // إعادة تحميل القائمة
            } else {
                throw new Error(response.data?.message || 'فشل في معالجة الدعوة');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في معالجة الدعوة');
        } finally {
            setProcessingInvitation(null);
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل الدعوات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container my-5">
             

                {/* Invitations Table */}
                {invitations.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-5">
                        <FaEnvelope size={64} className="text-muted mb-3" />
                        <h5 className="text-muted">لا توجد دعوات</h5>
                        <p className="text-muted">لم يتم إرسال أي دعوات لك بعد</p>
                    </motion.div>
                ) : (
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">العنوان</th>
                                            <th className="text-center">التخصص</th>
                                            <th className="text-center">الحالة</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                        {invitations.map((invitation, index) => {
                                const specializations = getSpecializations(invitation);
                                return (
                                            <motion.tr
                                key={invitation.id || index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="table-row-modern">
                                                <td className="text-center">
                                                    <div className="invitation-number-badge">
                                                        #{index + 1}
                                            </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="invitation-title">
                                                        {invitation.title || 'غير محدد'}
                                            </div>
                                                </td>
                                                <td className="text-center">
                                                            <>
                                                                {specializations.length > 0 && (
                                                                    <div className="invitation-specializations">
                                                                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                                                                            {specializations.map((spec, specIndex) => (
                                                                                <span key={`${invitation.id || index}-spec-${specIndex}`} className="invitation-specialization-badge">
                                                                                    {spec}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {invitation.description ? (
                                                                    <p className={`invitation-description ${specializations.length > 0 ? 'mt-2' : ''} mb-0`}>
                                                                        {invitation.description}
                                                                    </p>
                                                                ) : (
                                                                    <span className="text-muted d-block mt-2">
                                                                        {specializations.length > 0 ? 'لا يوجد وصف' : 'غير محدد'}
                                                                    </span>
                                                                )}
                                                            </>
                                                </td>
                                                <td className="text-center">
                                                {getStatusBadge(invitation.status)}
                                                </td>
                                                <td className="text-center">
                                                    {invitation.status === 'pending' ? (
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={() => handleInvitationAction(invitation.id, 'accepted')}
                                                                disabled={processingInvitation === invitation.id}
                                                                title="قبول الدعوة"
                                                            >
                                                                {processingInvitation === invitation.id ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                ) : (
                                                                    <><FaCheck className="mx-1" /> قبول</>
                                                                )}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleInvitationAction(invitation.id, 'rejected')}
                                                                disabled={processingInvitation === invitation.id}
                                                                title="رفض الدعوة"
                                                            >
                                                                {processingInvitation === invitation.id ? (
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                ) : (
                                                                    <><FaTimes className="mx-1" /> رفض</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                                            </div>
                                            </div>
                                                </div>
                                            )}

                {/* Pagination */}
                {pagination && invitations.length > 0 && (
                    <div className="card-footer py-2 px-2">
                        <div className="row g-0">
                            <div className="col-md-6 px-2 d-flex align-items-center">
                                <span className="text-muted small">
                                    صفحة {pagination.current_page} من {pagination.pages_count}
                                </span>
                                                        </div>
                            <div className="col-md-6 px-2">
                                <div className="d-flex justify-content-end gap-1 align-items-center">
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}>
                                        الأولى
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}>
                                        السابق
                                    </button>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm text-center"
                                        style={{ width: '70px' }}
                                        value={currentPage}
                                        min="1"
                                        max={pagination.pages_count}
                                        onChange={(e) => {
                                            const page = parseInt(e.target.value);
                                            if (page >= 1 && page <= pagination.pages_count) {
                                                setCurrentPage(page);
                                            }
                                        }}
                                    />
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage === pagination.pages_count}>
                                        التالي
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setCurrentPage(pagination.pages_count)}
                                        disabled={currentPage === pagination.pages_count}>
                                        الأخيرة
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* CSS للتصميم */}
            <style jsx>{`
                .table-modern {
                    border-collapse: separate;
                    border-spacing: 0;
                }
                
                .table-modern th {
                    background: transparent;
                    color: #374151;
                    font-weight: 600;
                    border: none;
                    padding: 15px 10px;
                    font-size: 0.9rem;
                    text-align: center;
                }
                
                .table-modern td {
                    padding: 15px 10px;
                    border-bottom: 1px solid #e9ecef;
                    vertical-align: middle;
                    text-align: center;
                }
                
                .table-modern th:nth-child(4),
                .table-modern td:nth-child(4) {
                    width: 120px !important;
                    min-width: 120px !important;
                    max-width: 120px !important;
                }
                
                .table-row-modern:hover {
                    background-color: rgba(111, 66, 193, 0.08);
                    transform: translateY(-1px);
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(111, 66, 193, 0.1);
                }
                
                .invitation-number-badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.875rem;
                    display: inline-block;
                    min-width: 60px;
                    text-align: center;
                    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
                }
                
                .invitation-title {
                    font-weight: 600;
                    color: #374151;
                    font-size: 0.95rem;
                }
                
                .invitation-specializations {
                    min-height: 38px;
                }

                .invitation-specialization-badge {
                    background-color: #ffd200;
                    color: #1f2937;
                    font-weight: 600;
                    padding: 0.35rem 0.75rem;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 4px rgba(255, 210, 0, 0.3);
                }

                .invitation-description {
                    color: #6b7280;
                    font-size: 0.875rem;
                    margin: 0;
                    line-height: 1.6;
                    white-space: normal;
                }
                
                .badge {
                    font-size: 0.65rem !important;
                    padding: 0.3em 0.6em !important;
                    white-space: nowrap;
                }
            `}</style>
        </div>
    );
};

export default DoctorInvitations;
